import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BackInStockItemDetails,
  BackInStockNotificationRequest,
  BackInStockNotificationRequestCreatedEnvelope,
  BackInStockNotificationRequestDeletedEnvelope,
  BackInStockNotificationRequestQuery,
  BackInStockNotificationRequestUpdatedEnvelope,
  CatalogReference,
  CreateBackInStockNotificationRequestApplicationErrors,
  GetBackInStockNotificationRequestsCountByCatalogReferencesResponse,
  MarkAsNotificationSentApplicationErrors,
  MarkAsNotificationSentResponse,
  QueryBackInStockNotificationRequestsResponse,
  ReportItemsBackInStockApplicationErrors,
  ReportItemsBackInStockOptions,
  RequestsQueryBuilder,
  createBackInStockNotificationRequest as universalCreateBackInStockNotificationRequest,
  deleteBackInStockNotificationRequest as universalDeleteBackInStockNotificationRequest,
  getBackInStockNotificationRequest as universalGetBackInStockNotificationRequest,
  getBackInStockNotificationRequestsCountByCatalogReferences as universalGetBackInStockNotificationRequestsCountByCatalogReferences,
  markAsNotificationSent as universalMarkAsNotificationSent,
  queryBackInStockNotificationRequests as universalQueryBackInStockNotificationRequests,
  reportItemsBackInStock as universalReportItemsBackInStock,
  typedQueryBackInStockNotificationRequests as universalTypedQueryBackInStockNotificationRequests,
} from './ecom-v1-back-in-stock-notification-request-back-in-stock-notifications.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createBackInStockNotificationRequest(
  httpClient: HttpClient
): CreateBackInStockNotificationRequestSignature {
  return (
    request: NonNullablePaths<
      BackInStockNotificationRequest,
      | `catalogReference`
      | `catalogReference.appId`
      | `catalogReference.catalogItemId`
      | `email`,
      3
    >,
    itemDetails: NonNullablePaths<BackInStockItemDetails, `name` | `price`, 2>
  ) =>
    universalCreateBackInStockNotificationRequest(
      request,
      itemDetails,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateBackInStockNotificationRequestSignature {
  /**
   * Creates a back in stock notification request.
   *
   * If a notification request already exists for the same `catalogReference` and `email`,
   * then a new one isn't created and the existing request is returned.
   *
   * Back in stock notifications only work for [Wix Stores](https://support.wix.com/en/article/wix-stores-about-wix-stores).
   * The `appId` for Wix Stores is `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.
   * @param - Notification request information.
   *
   * Includes details for the out of stock item and the email address
   * requesting to be notified when it's back in stock.
   * @param - Item details to include in the notification when the item is back in stock.
   * @returns Created back in stock notification request.
   */
  (
    request: NonNullablePaths<
      BackInStockNotificationRequest,
      | `catalogReference`
      | `catalogReference.appId`
      | `catalogReference.catalogItemId`
      | `email`,
      3
    >,
    itemDetails: NonNullablePaths<BackInStockItemDetails, `name` | `price`, 2>
  ): Promise<
    NonNullablePaths<
      BackInStockNotificationRequest,
      | `catalogReference.catalogItemId`
      | `catalogReference.appId`
      | `email`
      | `status`,
      3
    > & {
      __applicationErrorsType?: CreateBackInStockNotificationRequestApplicationErrors;
    }
  >;
}

export function getBackInStockNotificationRequest(
  httpClient: HttpClient
): GetBackInStockNotificationRequestSignature {
  return (_id: string) =>
    universalGetBackInStockNotificationRequest(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBackInStockNotificationRequestSignature {
  /**
   * Retrieves a back in stock notification request.
   * @param - ID of the notification request to retrieve.
   * @returns Retrieved back in stock notification request.
   */
  (_id: string): Promise<
    NonNullablePaths<
      BackInStockNotificationRequest,
      | `catalogReference.catalogItemId`
      | `catalogReference.appId`
      | `email`
      | `status`,
      3
    >
  >;
}

export function deleteBackInStockNotificationRequest(
  httpClient: HttpClient
): DeleteBackInStockNotificationRequestSignature {
  return (_id: string) =>
    universalDeleteBackInStockNotificationRequest(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteBackInStockNotificationRequestSignature {
  /**
   * Deletes a back in stock notification request.
   * @param - ID of the notification request to delete.
   */
  (_id: string): Promise<void>;
}

export function markAsNotificationSent(
  httpClient: HttpClient
): MarkAsNotificationSentSignature {
  return (_id: string) =>
    universalMarkAsNotificationSent(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface MarkAsNotificationSentSignature {
  /**
   * Sets `status` of a back in stock request to `NOTIFICATION_SENT`.
   *
   * Use this endpoint if the notification is sent manually offline. If the notification is sent
   * automatically or with the Report Items Back In Stock method, then `status` updates on its own.
   * @param - ID of the notification request to mark.
   */
  (_id: string): Promise<
    NonNullablePaths<
      MarkAsNotificationSentResponse,
      | `request.catalogReference.catalogItemId`
      | `request.catalogReference.appId`
      | `request.email`
      | `request.status`,
      4
    > & {
      __applicationErrorsType?: MarkAsNotificationSentApplicationErrors;
    }
  >;
}

export function queryBackInStockNotificationRequests(
  httpClient: HttpClient
): QueryBackInStockNotificationRequestsSignature {
  return () =>
    universalQueryBackInStockNotificationRequests(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryBackInStockNotificationRequestsSignature {
  /**
   * Creates a query to retrieve a list of back in stock notification requests.
   *
   * The `queryBackInStockNotificationRequests()` method builds a query to retrieve a list of back in stock notification requests and returns a `RequestsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `RequestsQueryBuilder` methods onto the query. `RequestsQueryBuilder` methods enable you to sort, filter, and control the results that `queryBackInStockNotificationRequests()` returns.
   *
   * The following `RequestsQueryBuilder` methods are supported for `queryBackInStockNotificationRequests()`. For a full description of the Requests object, see the object returned for the `items` property in `RequestsQueryResult`."
   */
  (): RequestsQueryBuilder;
}

export function typedQueryBackInStockNotificationRequests(
  httpClient: HttpClient
): TypedQueryBackInStockNotificationRequestsSignature {
  return (query: BackInStockNotificationRequestQuery) =>
    universalTypedQueryBackInStockNotificationRequests(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryBackInStockNotificationRequestsSignature {
  /** */
  (query: BackInStockNotificationRequestQuery): Promise<
    NonNullablePaths<
      QueryBackInStockNotificationRequestsResponse,
      | `requests`
      | `requests.${number}.catalogReference.catalogItemId`
      | `requests.${number}.catalogReference.appId`
      | `requests.${number}.email`
      | `requests.${number}.status`,
      5
    >
  >;
}

export function getBackInStockNotificationRequestsCountByCatalogReferences(
  httpClient: HttpClient
): GetBackInStockNotificationRequestsCountByCatalogReferencesSignature {
  return (catalogReferences: CatalogReference[]) =>
    universalGetBackInStockNotificationRequestsCountByCatalogReferences(
      catalogReferences,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBackInStockNotificationRequestsCountByCatalogReferencesSignature {
  /**
   * Retrieves the amount of back in stock requests for a given `catalogReference` item.
   * @param - `catalogReference` items to retrieve the notification request for.
   */
  (catalogReferences: CatalogReference[]): Promise<
    NonNullablePaths<
      GetBackInStockNotificationRequestsCountByCatalogReferencesResponse,
      | `countsPerCatalogReference`
      | `countsPerCatalogReference.${number}.catalogReference.catalogItemId`
      | `countsPerCatalogReference.${number}.catalogReference.appId`
      | `countsPerCatalogReference.${number}.count`,
      5
    >
  >;
}

export function reportItemsBackInStock(
  httpClient: HttpClient
): ReportItemsBackInStockSignature {
  return (
    itemDetails: NonNullablePaths<BackInStockItemDetails, `name` | `price`, 2>,
    options?: ReportItemsBackInStockOptions
  ) =>
    universalReportItemsBackInStock(
      itemDetails,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ReportItemsBackInStockSignature {
  /**
   * Sends notifications for back in stock requests.
   *
   * > **Important:**
   * > Automations must be turned on in a [Wix user's dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Go%20to%20Back-in-Stock&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https://www.wix.com/dashboard/{{metaSiteId}}/store/back-in-stock) for notifications to send.
   *
   * This endpoint triggers notifications for requests in 1 of 2 ways:
   * 1. For a specific item, with the `catalogReference` information.
   * 2. For specific requests, with `requestIds`.
   *
   * `itemDetails` are required and may populate dynamic values in the notification template, as follows:
   * + `itemDetails.name` passes to the template as `item.name`
   * + `itemDetails.price` passes to the template as `item.price`
   * + `itemDetails.image.url` passes to the template as `item.image.url`
   *
   * If the notification template doesn't include `item.price`, `item.name`, or `item.image.url`, values should
   * be passed in `extraAutomationTemplateParameters`.
   *
   * After this endpoint is called, the `status` for the request will update to `NOTIFICATION_SENT` if it sends
   * successfully, or to `FAILED` if it fails to send.
   * @param - Item details to use in notifications.
   *
   * `itemDetails` may populate dynamic values in the notification template, as follows:
   * + `itemDetails.name` passes to the template as `item.name`
   * + `itemDetails.price` passes to the template as `item.price`
   * + `itemDetails.image.url` passes to the template as `item.image.url`
   *
   * Use `extraAutomationTemplateParameters` to pass additional dynamic values.
   * @param - Report options.
   */
  (
    itemDetails: NonNullablePaths<BackInStockItemDetails, `name` | `price`, 2>,
    options?: ReportItemsBackInStockOptions
  ): Promise<
    void & {
      __applicationErrorsType?: ReportItemsBackInStockApplicationErrors;
    }
  >;
}

export const onBackInStockNotificationRequestCreated = EventDefinition(
  'wix.ecom.v1.back_in_stock_notification_request_created',
  true,
  (event: BackInStockNotificationRequestCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BackInStockNotificationRequestCreatedEnvelope>();
export const onBackInStockNotificationRequestDeleted = EventDefinition(
  'wix.ecom.v1.back_in_stock_notification_request_deleted',
  true,
  (event: BackInStockNotificationRequestDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BackInStockNotificationRequestDeletedEnvelope>();
export const onBackInStockNotificationRequestUpdated = EventDefinition(
  'wix.ecom.v1.back_in_stock_notification_request_updated',
  true,
  (event: BackInStockNotificationRequestUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BackInStockNotificationRequestUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  BackInStockItemDetails,
  BackInStockNotificationRequest,
  BackInStockNotificationRequestCreatedEnvelope,
  BackInStockNotificationRequestDeletedEnvelope,
  BackInStockNotificationRequestQuerySpec,
  BackInStockNotificationRequestUpdatedEnvelope,
  BackInStockNotificationRequestsCount,
  BaseEventMetadata,
  CatalogReference,
  CreateBackInStockNotificationRequestRequest,
  CreateBackInStockNotificationRequestResponse,
  CursorPaging,
  Cursors,
  CustomTag,
  DeleteBackInStockNotificationRequestRequest,
  DeleteBackInStockNotificationRequestResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  File,
  FocalPoint,
  GetBackInStockNotificationRequestRequest,
  GetBackInStockNotificationRequestResponse,
  GetBackInStockNotificationRequestsCountByCatalogReferencesRequest,
  GetBackInStockNotificationRequestsCountByCatalogReferencesResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  MarkAsNotificationSentRequest,
  MarkAsNotificationSentResponse,
  MessageEnvelope,
  Page,
  Pages,
  PlatformPaging,
  PlatformPagingMetadata,
  PlatformQuery,
  PlatformQueryPagingMethodOneOf,
  QueryBackInStockNotificationRequestsRequest,
  QueryBackInStockNotificationRequestsResponse,
  ReportItemsBackInStockOptions,
  ReportItemsBackInStockRequest,
  ReportItemsBackInStockResponse,
  RequestsQueryBuilder,
  RequestsQueryResult,
  RestoreInfo,
  SortOrder,
  Sorting,
  Status,
  URI,
  URIs,
  WebhookIdentityType,
  utils,
} from './ecom-v1-back-in-stock-notification-request-back-in-stock-notifications.universal.js';
